import React from 'react';
import clsx from 'clsx';
import { Link } from 'react-router-dom';
import {
  CargoPreviewPlanOutDTO,
  ExpeditionPreviewOutDTO,
  ExpeditionStatusEnum,
  OrderPreviewExpeditionListOutDTO,
  PriceDTO,
  TransportationTypeEnum,
} from '@api/ringil-api.v2';
import { enc, isNotBlank, isNotNullOrUndefined } from '@utils/utils';
import { dayjsFromApiUtcInTz, stringDateTimeWithLocaleFromApiStringInTz } from '@utils/dateWithTz.tsx';
import { formatPrice, sumPriceDTOs } from '@utils/priceUtils.ts';
import { truncateFileName } from '@utils/fileUtils.ts';
import type { SecuredFlag } from '@components/security/types';
import SecuredArea from '@components/security/SecuredArea';
import EllipsisText from '@components/RingilComponents/EllipsisText';
import SupplierNotificationBadge from '@components/RingilComponents/badges/SupplierNotificationBadge';
import PriceTag from '@components/RingilComponents/tags/priceTag/PriceTag.tsx';
import SafeHtml from '@components/RingilComponents/RichTextEditor/SafeHtml.tsx';
import { noteToPlainText } from '@components/RingilComponents/RichTextEditor/sanitizeNoteHtml';
import ExpeditionStateSpan from '@components/obsolete/StateSpan/ExpeditionStateSpan.tsx';
import COTimezone from '@components/FigmaComponents/COTimezone.tsx';
import ExpandableLabelWrapper from '@components/RingilDataTable/ExpandableLabelWrapper.tsx';
import ExpandableInfoAllTag from '@components/RingilDataTable/ExpandableInfoAllTag.tsx';
import ExpandableInfoTag from '@components/RingilDataTable/ExpandableInfoTag.tsx';
import AttachmentLink from '@components/RingilDataTable/AttachmentLink.tsx';
import { AiChatClassificationButton } from '@components/ai/AiChatClassificationButton.tsx';
import { getTransportIcon, joinLegInfo } from '@app/pages/carrier/demand-detail/carrierQuoteUtils.tsx';
import type { FieldDefinition } from '@components/RingilDataTable/v3/columns/buildColumns';
import AlertCircle from '@icons/AlertCircle.tsx';
import SvgChevron from '@icons/chevron.svg?react';
import SvgStar from '@icons/star-inverted.svg?react';
import SvgFile from '@icons/file-inverted.svg?react';
import SvgCheckSquare from '@icons/check-square.svg?react';
import SvgTruck from '@icons/truck.svg?react';
import SvgLink from '@icons/link.svg?react';
import SvgOrder from '@icons/order.svg?react';
import SvgUser from '@icons/user-inverted.svg?react';
import SvgMoney from '@icons/money-inverted.svg?react';
import SvgLayers from '@icons/layers-inverted.svg?react';
import SvgNumber from '@icons/number.svg?react';
import SvgCustoms from '@icons/Customs.svg?react';
import SvgRobot from '@icons/ai-robot.svg?react';
import SvgRoute from '@components/ringil3/icons/heading/Route';
import { InfoTagEnum } from '../infoTags';
import { timeslotDateRange, timeslotTimeRange } from './timeslotParts';
import { ExpeditionField, ExpeditionField as F, ExpeditionFieldGroup as Group, infoTagsCollapsible } from './expeditionFieldKeys';

/**
 * The expedition list's field catalogue for RingilDataTable v3 (RD-2957).
 *
 * Every field is a piece of one of today's six cells, rendered with the component that cell uses —
 * a status stays a coloured chip, a price a PriceTag, a note a tag — so taking a cell apart never
 * downgrades a value to plain text. The presets below put the pieces back together, and the default
 * tab is made of them, so it shows what today's list shows. Test ids are the cells' own.
 *
 * Only data the list payload already carries is here; anything new is backend work.
 */

export interface ExpeditionFieldContext {
  tr: (key: string, fallback: string, params?: Record<string, unknown>) => string;
  detailLink: (expeditionId: string) => string;
  manifestLink: (manifestId: string) => string;
  /** The user's timezone, for the tender validity and "created at". */
  timezoneId: string;
  flags: { price: SecuredFlag; extraCosts: SecuredFlag; aiClassification: SecuredFlag; aiDispatcher: SecuredFlag };
  /** The list's remembered expanded state of the additional-info tags. */
  infoTags: { isExpanded: (tag: InfoTagEnum) => boolean; toggle: (tag: InfoTagEnum) => void; setAll: (expanded: boolean) => void };
  labels: { expanded: boolean; toggle: () => void };
  transportTypeName: (type: string) => string;
  onAttachmentClick: (attachmentId: string, fileName: string) => void;
}

type Row = ExpeditionPreviewOutDTO;

const firstLocation = (row: Row) => row.locations?.[0];
/** Only a second location is an unloading place; a single-stop shipment has none. */
const lastLocation = (row: Row) => (row.locations?.length > 1 ? row.locations[row.locations.length - 1] : undefined);

// Ringil 2.0 caption: 12/16, secondary — the second line of a cell (Figma CO Table Cell).
const muted = 'text-xs leading-4 text-content-secondary';

const handlingUnits = (orders: OrderPreviewExpeditionListOutDTO[]) => orders.flatMap(order => order.items || []).reduce((sum, item) => sum + (item.amount || 0), 0);

const carInfoText = (row: Row) => [row.assignedDriver, row.carInfo?.carPlate].filter(v => isNotBlank(v)).join(', ');

const totalPriceDto = (row: Row): PriceDTO | undefined => {
  const prices = [row.expeditionPriceDTO, ...(row.extraCosts?.flatMap(cost => cost.priceDTO) ?? [])].filter(p => p != null) as PriceDTO[];
  return prices.length > 0 ? sumPriceDTOs(prices) : undefined;
};

/** Same condition as the v2 "expand all" tag: anything in the additional-info cell to expand. */
const hasAdditionalInfo = (row: Row, extraCostsGranted: boolean, aiDispatcherGranted: boolean) =>
  row.isFavorite ||
  !!row.expeditionType ||
  !!row.createdByFullName ||
  row.orders?.length > 0 ||
  isNotBlank(row.note) ||
  (extraCostsGranted && row.extraCosts?.length > 0) ||
  isNotBlank(carInfoText(row)) ||
  row.locations?.length > 2 ||
  row.isInvoiced ||
  row.attachmentInfo?.attachmentSize > 0 ||
  !!row.pairingSymbol ||
  !!row.trackingNumber ||
  !!row.transportationType ||
  (aiDispatcherGranted && !!row.aiDispatcherLastChangeAt);

/**
 * A plain value in a cell: one line, or up to `lines` for long-form text (names, places), ending in an
 * ellipsis with the full text on hover once the column is narrower than the value. Columns are
 * resizable, so text never wraps mid-word or widens the column instead.
 */
const text = (value?: string | number | null, className?: string, lines = 1) =>
  isNotNullOrUndefined(value) && value !== '' ? (
    <EllipsisText className={className} lines={lines}>
      {String(value)}
    </EllipsisText>
  ) : null;

const flagMark = (value: boolean) => (value ? <SvgCheckSquare className="text-complementary-success" /> : null);

/**
 * Builds the catalogue. Called on every render of the list, so each field closes over the list's
 * current state (expanded tags and labels) — never cache the result.
 */
export const buildExpeditionFields = (ctx: ExpeditionFieldContext): FieldDefinition<Row>[] => {
  const fields = expeditionFieldDefinitions(ctx);
  if (import.meta.env.DEV) {
    // A key without a definition would be offered nowhere and dropped from every stored tab on load.
    const missing = Object.values(ExpeditionField).filter(key => !fields.some(field => field.key === key));
    if (missing.length > 0) console.error('Expedition v3 catalogue: keys without a field definition', missing);
  }
  return fields;
};

const expeditionFieldDefinitions = (ctx: ExpeditionFieldContext): FieldDefinition<Row>[] => {
  const { tr, infoTags } = ctx;
  const tag = (key: InfoTagEnum) => ({ expanded: infoTags.isExpanded(key), onClick: () => infoTags.toggle(key) });

  return [
    // ------------------------------------------------------------------ shipment
    {
      key: F.ApplicationId,
      group: Group.Shipment,
      header: 'AllExpedition.enterExpeditionId',
      headerDefault: 'ID expedice',
      sortField: 'applicationId',
      minWidth: 120,
      identity: true,
      required: true,
      render: row => (
        <>
          <span
            data-testid={`customer-expeditions-row-${row.applicationId}`}
            data-application-id={row.applicationId}
            data-expedition-id={row.id}
            style={{ display: 'none' }}
          />
          {/* data-row-open: in the split view a plain click opens the pane; Ctrl/Cmd-click still opens a new tab. */}
          <Link to={ctx.detailLink(row.id)} className="block min-w-0 font-medium" data-row-open>
            <EllipsisText>{row.applicationId}</EllipsisText>
          </Link>
        </>
      ),
    },
    {
      key: F.ShipmentName,
      group: Group.Shipment,
      header: 'generic.title',
      headerDefault: 'Name',
      minWidth: 160,
      render: row => (row.name ? <EllipsisText lines={2}>{row.name}</EllipsisText> : null),
    },
    {
      key: F.SupplierManifest,
      group: Group.Shipment,
      header: 'generic.supplierManifest',
      headerDefault: 'Supplier manifest',
      minWidth: 120,
      render: row =>
        row.supplierManifestId ? (
          <Link to={ctx.manifestLink(row.supplierManifestId)} className="block min-w-0">
            <EllipsisText>{row.supplierManifestAppId}</EllipsisText>
          </Link>
        ) : null,
    },
    {
      key: F.ResponsiblePerson,
      group: Group.Shipment,
      header: 'generic.responsiblePerson',
      headerDefault: 'Responsible person',
      minWidth: 140,
      render: row =>
        row.responsiblePerson ? (
          <div className={muted} data-testid={`customer-expeditions-row-${row.applicationId}-responsiblePerson`}>
            <EllipsisText>{row.responsiblePerson}</EllipsisText>
          </div>
        ) : null,
    },
    {
      key: F.SupplierNotification,
      group: Group.Shipment,
      header: 'generic.supplierNotification',
      headerDefault: 'Supplier notification',
      minWidth: 140,
      fitWidth: 130,
      render: row =>
        row.supplierNotificationId ? (
          <SupplierNotificationBadge supplierNotificationId={row.supplierNotificationId} supplierNotificationAppId={row.supplierNotificationAppId} />
        ) : null,
    },
    {
      key: F.Labels,
      group: Group.Shipment,
      header: 'generic.labels',
      headerDefault: 'Labels',
      minWidth: 160,
      render: row => (
        <SecuredArea id={`row-labels-${row.applicationId}`} visible={[{ type: 'data', label: 'labels.length > 0', satisfied: row.labels?.length > 0 }]} label="Labels">
          <ExpandableLabelWrapper labels={row.labels} isExpanded={ctx.labels.expanded} onToggle={ctx.labels.toggle} />
        </SecuredArea>
      ),
    },
    {
      key: F.AiClassification,
      group: Group.Shipment,
      header: 'generic.aiClassification',
      headerDefault: 'AI classification',
      minWidth: 140,
      secured: ctx.flags.aiClassification,
      fitWidth: 110,
      render: (row, toggleExpandedRow) => (
        <SecuredArea
          id={`row-ai-classification-${row.applicationId}`}
          visible={[ctx.flags.aiClassification, { type: 'data', label: 'row.aiChatClassification present', satisfied: !!row.aiChatClassification }]}
          label="AI chat classification badge"
        >
          <div onClick={() => toggleExpandedRow?.(row)}>
            <AiChatClassificationButton aiChatClassification={row.aiChatClassification} />
          </div>
        </SecuredArea>
      ),
    },

    // ------------------------------------------------------------------ state
    {
      key: F.Status,
      group: Group.State,
      header: 'AllExpedition.stateHeaderTitle',
      headerDefault: 'Stav',
      minWidth: 120,
      fitWidth: 110,
      render: row => (
        <>
          <span data-testid={`customer-expeditions-row-${row.applicationId}-state`} data-expedition-status={row.expeditionStatus} style={{ display: 'none' }} />
          <ExpeditionStateSpan expeditionStage={row.expeditionStatus} isManual={row.isManualExpedition} />
        </>
      ),
    },
    {
      key: F.ManualMode,
      group: Group.State,
      header: 'AllExpedition.manual',
      headerDefault: 'Manual',
      minWidth: 90,
      render: row => (row.isManualExpedition ? text(tr('AllExpedition.manual', 'Manual'), muted) : null),
    },

    // ------------------------------------------------------------------ route
    {
      key: F.LoadingLocation,
      group: Group.Route,
      header: 'generic.loadingPlace',
      headerDefault: 'Loading place',
      minWidth: 160,
      render: row => <div data-testid={`customer-expeditions-row-${row.applicationId}-route-from`}>{text(firstLocation(row)?.name, undefined, 2)}</div>,
    },
    {
      key: F.LoadingDate,
      group: Group.Route,
      header: 'generic.loadingDate',
      headerDefault: 'Loading date',
      minWidth: 140,
      render: row => text(timeslotDateRange(firstLocation(row)?.timeslots), muted),
    },
    {
      key: F.LoadingTime,
      group: Group.Route,
      header: 'generic.loadingTime',
      headerDefault: 'Loading time',
      minWidth: 110,
      render: row => text(timeslotTimeRange(firstLocation(row)?.timeslots), muted),
    },
    {
      key: F.LoadingTimezone,
      group: Group.Route,
      header: 'generic.loadingTimezone',
      headerDefault: 'Loading timezone',
      minWidth: 120,
      fitWidth: 100,
      render: row =>
        firstLocation(row)?.timeslots?.timezoneId ? (
          <COTimezone timezoneId={firstLocation(row).timeslots.timezoneId} dataTestId={`customer-expeditions-row-${row.applicationId}-route-from-timezone`} />
        ) : null,
    },
    {
      key: F.UnloadingLocation,
      group: Group.Route,
      header: 'generic.unloadingPlace',
      headerDefault: 'Unloading place',
      minWidth: 160,
      render: row =>
        lastLocation(row) ? <div data-testid={`customer-expeditions-row-${row.applicationId}-route-to`}>{text(lastLocation(row).name, undefined, 2)}</div> : null,
    },
    {
      key: F.UnloadingDate,
      group: Group.Route,
      header: 'generic.unloadingDate',
      headerDefault: 'Unloading date',
      minWidth: 140,
      render: row => text(timeslotDateRange(lastLocation(row)?.timeslots), muted),
    },
    {
      key: F.UnloadingTime,
      group: Group.Route,
      // Not AllExpedition.unloadingTime: that existing key is the list's unloading-DATE filter ("Vykládka").
      header: 'generic.unloadingTime',
      headerDefault: 'Unloading time',
      minWidth: 110,
      render: row => text(timeslotTimeRange(lastLocation(row)?.timeslots), muted),
    },
    {
      key: F.UnloadingTimezone,
      group: Group.Route,
      header: 'generic.unloadingTimezone',
      headerDefault: 'Unloading timezone',
      minWidth: 120,
      fitWidth: 100,
      render: row =>
        lastLocation(row)?.timeslots?.timezoneId ? (
          <COTimezone timezoneId={lastLocation(row).timeslots.timezoneId} dataTestId={`customer-expeditions-row-${row.applicationId}-route-to-timezone`} />
        ) : null,
    },

    // ------------------------------------------------------------------ tender
    {
      key: F.TotalPrice,
      group: Group.Tender,
      header: 'generic.totalPrice',
      headerDefault: 'Total price',
      minWidth: 110,
      secured: ctx.flags.price,
      // Base price plus extra costs, shown once a carrier is assigned — the v2 tender cell's total.
      fitWidth: 100,
      render: row => {
        if (!row.assignedCarrierName) return null;
        const total = row.expeditionPrice + (row.extraCosts?.reduce((a, b) => a + b.price, 0) ?? 0);
        return (
          <>
            <span
              data-testid={`customer-expeditions-row-${row.applicationId}-assigned-price`}
              data-assigned-total-price={String(total)}
              data-assigned-currency={row.currency ?? ''}
              style={{ display: 'none' }}
            />
            <SecuredArea id={`row-price-${row.applicationId}`} visible={ctx.flags.price} label="Shipment price">
              <PriceTag priceDto={totalPriceDto(row)} />
            </SecuredArea>
          </>
        );
      },
    },
    {
      key: F.BasePrice,
      group: Group.Tender,
      header: 'generic.basePrice',
      headerDefault: 'Price without extra costs',
      minWidth: 110,
      secured: ctx.flags.price,
      fitWidth: 100,
      render: row => (row.assignedCarrierName && row.expeditionPriceDTO ? <PriceTag priceDto={row.expeditionPriceDTO} /> : null),
    },
    {
      key: F.AssignedCarrier,
      group: Group.Tender,
      header: 'generic.carrier',
      headerDefault: 'Carrier',
      minWidth: 150,
      render: row =>
        row.assignedCarrierName ? (
          <div className="flex min-w-0 items-center gap-1">
            <SvgTruck className="shrink-0" />
            <span className="min-w-0" data-testid={`customer-expeditions-row-${row.applicationId}-assigned-carrierName`}>
              <EllipsisText>{row.assignedCarrierName}</EllipsisText>
            </span>
          </div>
        ) : null,
    },
    {
      key: F.CarInfo,
      group: Group.Tender,
      header: 'generic.carInfo',
      headerDefault: 'Driver, car plate',
      minWidth: 140,
      render: row =>
        row.assignedCarrierName && isNotBlank(carInfoText(row)) ? (
          <span className="tag-text block min-w-0" data-testid={`customer-expeditions-row-${row.applicationId}-car-info`}>
            <EllipsisText>{carInfoText(row)}</EllipsisText>
          </span>
        ) : null,
    },
    {
      key: F.TenderStatus,
      group: Group.Tender,
      header: 'generic.tenderStatus',
      headerDefault: 'Tender status',
      minWidth: 170,
      fitWidth: 170,
      render: row => <TenderStatus row={row} ctx={ctx} />,
    },

    // ------------------------------------------------------------------ additional info (tags)
    {
      key: F.InfoExpandAll,
      group: Group.AdditionalInfo,
      header: 'generic.expandAll',
      headerDefault: 'Expand all',
      inline: true,
      exportable: false,
      render: row => (
        <SecuredArea
          id={`row-tag-all-${row.applicationId}`}
          visible={[{ type: 'data', label: 'any additional info available', satisfied: !!hasAdditionalInfo(row, ctx.flags.extraCosts.granted, ctx.flags.aiDispatcher.granted) }]}
          label="Expand-all tag"
        >
          <ExpandableInfoAllTag
            id={`all_${enc(row.applicationId)}`}
            dataTestId={`customer-expeditions-row-${row.applicationId}-additional-all`}
            onClick={() => infoTags.setAll(!infoTags.isExpanded(InfoTagEnum.ALL))}
            tooltipText={infoTags.isExpanded(InfoTagEnum.ALL) ? tr('expandableInfoTag.collapseAll', 'Collapse all') : tr('expandableInfoTag.expandAll', 'Expand all')}
            icon={<SvgChevron className={clsx('text-content-brand', infoTags.isExpanded(InfoTagEnum.ALL) && 'rotate-180')} />}
          />
        </SecuredArea>
      ),
    },
    {
      key: F.Favorite,
      group: Group.AdditionalInfo,
      header: 'AllExpedition.favorite',
      headerDefault: 'Favorite',
      inline: true,
      render: (row, _toggleExpandedRow, cell) =>
        // Out of the additional-info column: the value alone, as any other column shows it.
        infoTagsCollapsible(cell.members) ? (
          <SecuredArea id={`row-tag-favorite-${row.applicationId}`} visible={[{ type: 'data', label: 'isFavorite', satisfied: row.isFavorite }]} label="Favorite tag">
            <ExpandableInfoTag
              id={`favorite_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-favorite`}
              {...tag(InfoTagEnum.FAVORITE)}
              label={tr('AllExpedition.favorite', 'Favorite')}
              icon={<SvgStar className={'text-yellow-400 fill-current'} />}
            />
          </SecuredArea>
        ) : (
          flagMark(row.isFavorite)
        ),
    },
    {
      key: F.ExpeditionType,
      group: Group.AdditionalInfo,
      header: 'generic.expeditionType',
      headerDefault: 'Expedition type',
      inline: true,
      render: (row, _toggleExpandedRow, cell) => {
        const expeditionType = row.expeditionType ? tr(`global.expeditionTypeShort${row.expeditionType}`, row.expeditionType) : null;
        // Out of the additional-info column: the value alone, as any other column shows it.
        if (!infoTagsCollapsible(cell.members)) return text(expeditionType);
        return (
          <SecuredArea
            id={`row-tag-type-${row.applicationId}`}
            visible={[{ type: 'data', label: 'expeditionType present', satisfied: !!expeditionType }]}
            label="Expedition type tag"
          >
            <ExpandableInfoTag
              id={`type_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-type`}
              {...tag(InfoTagEnum.TYPE)}
              iconText={expeditionType}
            />
          </SecuredArea>
        );
      },
    },
    {
      key: F.CreatedBy,
      group: Group.AdditionalInfo,
      header: 'AllExpedition.createdBy',
      headerDefault: 'Vytvořil',
      inline: true,
      render: (row, _toggleExpandedRow, cell) =>
        // Out of the additional-info column: the value alone, as any other column shows it.
        infoTagsCollapsible(cell.members) ? (
          <SecuredArea
            id={`row-tag-created-by-${row.applicationId}`}
            visible={[{ type: 'data', label: 'createdByFullName present', satisfied: !!row.createdByFullName }]}
            label="Created by tag"
          >
            <ExpandableInfoTag
              id={`createdBy_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-createdBy`}
              {...tag(InfoTagEnum.CREATED_BY)}
              label={tr('AllExpedition.createdByLabel', 'Created by: ')}
              text={row.createdByFullName}
              icon={<SvgUser />}
            />
          </SecuredArea>
        ) : (
          text(row.createdByFullName)
        ),
    },
    {
      key: F.Orders,
      group: Group.AdditionalInfo,
      header: 'generic.orders',
      headerDefault: 'Orders',
      inline: true,
      render: (row, _toggleExpandedRow, cell) => {
        const orders = row.orders ?? [];
        const ordersText = orders
          .map(order => {
            const items = order.items
              ?.filter(item => item.name)
              ?.map((item: CargoPreviewPlanOutDTO) => `${item.name}: ${item.amount}`)
              ?.join(', ');
            return order.orderName ? `${order.orderName}${items ? ' - ' + items : ''}` : '';
          })
          .join(' | ');
        const unitsByType = orders
          .flatMap(order => order.items || [])
          .reduce<Map<string, number>>((acc, item) => (item.name ? acc.set(item.name, (acc.get(item.name) || 0) + (item.amount || 0)) : acc), new Map());
        const unitsSummary = Array.from(unitsByType.entries())
          .map(([name, amount]) => `${name}: ${amount}`)
          .join(', ');
        // Out of the additional-info column: the value alone, as any other column shows it.
        if (!infoTagsCollapsible(cell.members)) return text(ordersText, undefined, 3);
        return (
          <SecuredArea
            id={`row-tag-orders-${row.applicationId}`}
            visible={[{ type: 'data', label: 'orders.length > 0', satisfied: orders.length > 0 }]}
            label="Orders tag"
          >
            <ExpandableInfoTag
              id={`orders_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-orders`}
              {...tag(InfoTagEnum.ORDERS)}
              label={tr('AllExpedition.ordersTagLabel', 'Orders: ')}
              text={ordersText}
              icon={<SvgOrder />}
              count={orders.length}
              secondaryCount={handlingUnits(orders)}
              extraText={unitsSummary ? `${tr('AllExpedition.unitsTagLabel', 'Units: ')}${unitsSummary}` : undefined}
            />
          </SecuredArea>
        );
      },
    },
    {
      key: F.Note,
      group: Group.AdditionalInfo,
      header: 'generic.note',
      headerDefault: 'Note',
      inline: true,
      render: (row, _toggleExpandedRow, cell) =>
        // Out of the additional-info column: the value alone, as any other column shows it.
        infoTagsCollapsible(cell.members) ? (
          <SecuredArea id={`row-tag-note-${row.applicationId}`} visible={[{ type: 'data', label: 'note present', satisfied: isNotBlank(row.note) }]} label="Note tag">
            <ExpandableInfoTag
              id={`note_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-noteForCarrier`}
              {...tag(InfoTagEnum.NOTE)}
              label={tr('AllExpedition.noteTagLabel', 'Note: ')}
              text={noteToPlainText(row.note)}
              textComponent={<SafeHtml html={row.note} />}
              icon={<SvgFile />}
            />
          </SecuredArea>
        ) : (
          text(noteToPlainText(row.note), undefined, 3)
        ),
    },
    {
      key: F.ExtraCosts,
      group: Group.AdditionalInfo,
      header: 'generic.extraCosts',
      headerDefault: 'Extra costs',
      inline: true,
      secured: ctx.flags.extraCosts,
      render: (row, _toggleExpandedRow, cell) =>
        // Out of the additional-info column: the value alone, as any other column shows it.
        infoTagsCollapsible(cell.members) ? (
          <SecuredArea
            id={`row-extra-costs-${row.applicationId}`}
            visible={[ctx.flags.extraCosts, { type: 'data', label: 'extraCosts.length > 0', satisfied: row.extraCosts?.length > 0 }]}
            label="Extra costs tag"
          >
            <ExpandableInfoTag
              id={`extraCosts_${enc(row.applicationId)}`}
              {...tag(InfoTagEnum.EXTRA_COSTS)}
              label={tr('AllExpedition.extraCostsTagLabel', 'Extra Costs: ')}
              text={row.extraCosts?.map(cost => `${cost.name}: ${formatPrice(cost.price, cost.currency)}`).join('; ') || ''}
              icon={<SvgMoney />}
              count={row.extraCosts?.length ?? 0}
            />
          </SecuredArea>
        ) : (
          text(row.extraCosts?.map(cost => `${cost.name}: ${formatPrice(cost.price, cost.currency)}`).join('; '), undefined, 3)
        ),
    },
    {
      key: F.Stops,
      group: Group.AdditionalInfo,
      header: 'generic.intermediateStops',
      headerDefault: 'Intermediate stops',
      inline: true,
      render: (row, _toggleExpandedRow, cell) => {
        const stopCount = (row.locations?.length ?? 0) - 2;
        // Out of the additional-info column: the value alone, as any other column shows it.
        if (!infoTagsCollapsible(cell.members))
          return text(
            row.locations
              ?.slice(1, -1)
              .map(loc => loc.name)
              .join(', '),
            undefined,
            3,
          );
        return (
          <SecuredArea
            id={`row-tag-stops-${row.applicationId}`}
            visible={[{ type: 'data', label: 'intermediate stops > 0', satisfied: stopCount > 0 }]}
            label="Stops tag"
          >
            <ExpandableInfoTag
              id={`stops_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-stops`}
              {...tag(InfoTagEnum.STOPS)}
              label={tr('AllExpedition.stopsTagLabel', 'Stops: ')}
              text={
                row.locations
                  ?.slice(1, -1)
                  .map(loc => loc.name)
                  .join(', ') || ''
              }
              icon={<SvgRoute />}
              count={stopCount}
            />
          </SecuredArea>
        );
      },
    },
    {
      key: F.Invoiced,
      group: Group.AdditionalInfo,
      header: 'AllExpedition.invoiced',
      headerDefault: 'Invoiced',
      inline: true,
      render: (row, _toggleExpandedRow, cell) =>
        // Out of the additional-info column: the value alone, as any other column shows it.
        infoTagsCollapsible(cell.members) ? (
          <SecuredArea id={`row-tag-invoiced-${row.applicationId}`} visible={[{ type: 'data', label: 'isInvoiced', satisfied: row.isInvoiced }]} label="Invoiced tag">
            <ExpandableInfoTag
              id={`invoiced_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-invoicingState`}
              {...tag(InfoTagEnum.INVOICED)}
              label={tr('AllExpedition.invoiced', 'Invoiced')}
              icon={<SvgCheckSquare className="text-complementary-success" />}
            />
          </SecuredArea>
        ) : (
          flagMark(row.isInvoiced)
        ),
    },
    {
      key: F.CustomsStatus,
      group: Group.AdditionalInfo,
      header: 'AllExpedition.customsStatus',
      headerDefault: 'Customs status',
      inline: true,
      render: (row, _toggleExpandedRow, cell) =>
        // Out of the additional-info column: the value alone, as any other column shows it.
        infoTagsCollapsible(cell.members) ? (
          <SecuredArea
            id={`row-tag-customs-status-${row.applicationId}`}
            visible={[{ type: 'data', label: 'customsStatus present', satisfied: !!row.customsStatus }]}
            label="Customs status tag"
          >
            <ExpandableInfoTag
              id={`customsStatus_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-customsStatus`}
              {...tag(InfoTagEnum.CUSTOMS_STATUS)}
              label={tr('AllExpedition.customsTagLabel', 'Customs: ')}
              text={row.customsStatus ? tr(`CustomsStatusTag.${row.customsStatus}`, row.customsStatus) : undefined}
              icon={<SvgCustoms />}
            />
          </SecuredArea>
        ) : (
          text(row.customsStatus ? tr(`CustomsStatusTag.${row.customsStatus}`, row.customsStatus) : null)
        ),
    },
    {
      key: F.Attachments,
      group: Group.AdditionalInfo,
      header: 'generic.attachments',
      headerDefault: 'Attachments',
      inline: true,
      render: (row, _toggleExpandedRow, cell) =>
        // Out of the additional-info column: the value alone, as any other column shows it.
        infoTagsCollapsible(cell.members) ? (
          <SecuredArea
            id={`row-tag-attachments-${row.applicationId}`}
            visible={[{ type: 'data', label: 'attachmentSize > 0', satisfied: row.attachmentInfo?.attachmentSize > 0 }]}
            label="Attachments tag"
          >
            <ExpandableInfoTag
              id={`attachments_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-attachments`}
              {...tag(InfoTagEnum.ATTACHMENTS)}
              icon={<SvgLink className={infoTags.isExpanded(InfoTagEnum.ATTACHMENTS) ? 'text-content-brand' : ''} />}
              label={tr('AllExpedition.attachmentTagLabel', 'Attachments: ')}
              count={row.attachmentInfo?.attachmentSize ?? 0}
              text={row.attachmentInfo?.attachments?.map(attachment => truncateFileName(attachment.name)).join(' | ')}
              textComponent={
                <span className="font-medium">
                  {row.attachmentInfo?.attachments?.map((attachment, idx) => (
                    <React.Fragment key={attachment.id}>
                      <AttachmentLink id={attachment.id} name={attachment.name} onActivate={ctx.onAttachmentClick} maxLength={15} />
                      {idx < row.attachmentInfo.attachments.length - 1 && <span className="text-content-tertiary">{' | '}</span>}
                    </React.Fragment>
                  ))}
                </span>
              }
            />
          </SecuredArea>
        ) : (
          // The one value that does something: each file still downloads on click.
          <span className="flex flex-wrap items-center gap-x-1 text-sm">
            {row.attachmentInfo?.attachments?.map((attachment, idx) => (
              <React.Fragment key={attachment.id}>
                <AttachmentLink id={attachment.id} name={attachment.name} onActivate={ctx.onAttachmentClick} maxLength={15} />
                {idx < row.attachmentInfo.attachments.length - 1 && <span className="text-content-tertiary">|</span>}
              </React.Fragment>
            ))}
          </span>
        ),
    },
    {
      key: F.TransportLegs,
      group: Group.AdditionalInfo,
      header: 'generic.transportLegs',
      headerDefault: 'Transport legs',
      inline: true,
      render: (row, _toggleExpandedRow, cell) =>
        // Out of the additional-info column: the value alone, as any other column shows it.
        infoTagsCollapsible(cell.members) ? (
          <SecuredArea
            id={`row-tag-transport-legs-${row.applicationId}`}
            visible={[{ type: 'data', label: 'bestQuoteTransportLegs.length > 0', satisfied: row.bestQuoteTransportLegs?.length > 0 }]}
            label="Transport legs tag"
          >
            <ExpandableInfoTag
              id={`transport_legs_${enc(row.applicationId)}`}
              {...tag(InfoTagEnum.TRANSPORT_LEGS)}
              icon={<SvgLayers />}
              label={tr('SpotQuoteDataTable.transportLegs', 'Transport legs: ')}
              text={row.bestQuoteTransportLegs?.map(leg => joinLegInfo(leg)).join(' | ')}
              textComponent={row.bestQuoteTransportLegs?.map((leg, idx) => (
                <div key={idx}>
                  <div className="flex flex-row gap-2 mt-2">
                    <div className="flex items-start justify-center pt-0.5">{getTransportIcon(leg.transportationType, 3)}</div>
                    <div className="flex flex-col text-content-primary font-dm-sans font-normal">
                      {tr('carrier.demand.quote.transportationType.' + leg.transportationType.toLowerCase() + '.prefix', `${leg.transportationType}: `)}
                      {joinLegInfo(leg)}
                    </div>
                  </div>
                </div>
              ))}
              count={row.bestQuoteTransportLegs?.length ?? 0}
            />
          </SecuredArea>
        ) : (
          text(row.bestQuoteTransportLegs?.map(leg => joinLegInfo(leg)).join(' | '), undefined, 3)
        ),
    },
    {
      key: F.PairingSymbol,
      group: Group.AdditionalInfo,
      header: 'AllExpedition.pairingSymbol',
      headerDefault: 'Pairing symbol',
      inline: true,
      render: (row, _toggleExpandedRow, cell) =>
        // Out of the additional-info column: the value alone, as any other column shows it.
        infoTagsCollapsible(cell.members) ? (
          <SecuredArea
            id={`row-tag-pairing-symbol-${row.applicationId}`}
            visible={[{ type: 'data', label: 'pairingSymbol present', satisfied: !!row.pairingSymbol }]}
            label="Pairing symbol tag"
          >
            <ExpandableInfoTag
              id={`pairingSymbol_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-pairingSymbol`}
              {...tag(InfoTagEnum.PAIRING_SYMBOL)}
              label={tr('AllExpedition.pairingSymbol', 'Pairing symbol')}
              text={row.pairingSymbol}
              icon={<SvgNumber />}
            />
          </SecuredArea>
        ) : (
          text(row.pairingSymbol)
        ),
    },
    {
      key: F.TrackingNumber,
      group: Group.AdditionalInfo,
      header: 'AllExpedition.trackingNumber',
      headerDefault: 'Tracking number',
      inline: true,
      render: (row, _toggleExpandedRow, cell) =>
        // Out of the additional-info column: the value alone, as any other column shows it.
        infoTagsCollapsible(cell.members) ? (
          <SecuredArea
            id={`row-tag-tracking-number-${row.applicationId}`}
            visible={[{ type: 'data', label: 'trackingNumber present', satisfied: !!row.trackingNumber }]}
            label="Tracking number tag"
          >
            <ExpandableInfoTag
              id={`trackingNumber_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-trackingNumber`}
              {...tag(InfoTagEnum.TRACKING_NUMBER)}
              label={tr('AllExpedition.trackingNumber', 'Tracking number')}
              text={row.trackingNumber}
              icon={<SvgNumber />}
            />
          </SecuredArea>
        ) : (
          text(row.trackingNumber)
        ),
    },
    {
      key: F.TransportationType,
      group: Group.AdditionalInfo,
      header: 'generic.transportationType',
      headerDefault: 'Transportation type',
      inline: true,
      render: (row, _toggleExpandedRow, cell) =>
        // Out of the additional-info column: the value alone, as any other column shows it.
        infoTagsCollapsible(cell.members) ? (
          <SecuredArea
            id={`row-tag-shipment-type-${row.applicationId}`}
            visible={[{ type: 'data', label: 'transportationType present', satisfied: !!row.transportationType }]}
            label="Shipment type tag"
          >
            <ExpandableInfoTag
              id={`shipmentType_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-shipmentType`}
              {...tag(InfoTagEnum.SHIPMENT_TYPE)}
              label={tr('AllExpedition.shipmentTypeTagLabel', 'Přeprava: ')}
              text={row.transportationType ? ctx.transportTypeName(row.transportationType) : undefined}
              icon={row.transportationType ? getTransportIcon(row.transportationType as unknown as TransportationTypeEnum, 4) : undefined}
            />
          </SecuredArea>
        ) : (
          text(row.transportationType ? ctx.transportTypeName(row.transportationType) : null)
        ),
    },
    {
      key: F.AiDispatcherChange,
      group: Group.AdditionalInfo,
      header: 'AllExpedition.aiDispatcherChange',
      headerDefault: 'AI Dispatcher change',
      inline: true,
      secured: ctx.flags.aiDispatcher,
      render: (row, _toggleExpandedRow, cell) => {
        const firstChange = row.aiDispatcherFirstChangeAt ? stringDateTimeWithLocaleFromApiStringInTz(row.aiDispatcherFirstChangeAt, ctx.timezoneId) : null;
        const lastChange = row.aiDispatcherLastChangeAt ? stringDateTimeWithLocaleFromApiStringInTz(row.aiDispatcherLastChangeAt, ctx.timezoneId) : null;
        return infoTagsCollapsible(cell.members) ? (
          <SecuredArea
            id={`row-tag-ai-dispatcher-change-${row.applicationId}`}
            visible={[ctx.flags.aiDispatcher, { type: 'data', label: 'aiDispatcherLastChangeAt present', satisfied: !!row.aiDispatcherLastChangeAt }]}
            label="AI Dispatcher change tag"
          >
            <ExpandableInfoTag
              id={`aiDispatcherChange_${enc(row.applicationId)}`}
              dataTestId={`customer-expeditions-row-${row.applicationId}-additional-aiDispatcherChange`}
              {...tag(InfoTagEnum.AI_DISPATCHER_CHANGE)}
              text={`${tr('AllExpedition.aiDispatcherFirstChange', 'First AI Dispatcher change')}: ${firstChange}`}
              extraText={`${tr('AllExpedition.aiDispatcherLastChange', 'Last AI Dispatcher change')}: ${lastChange}`}
              icon={<SvgRobot />}
            />
          </SecuredArea>
        ) : (
          text(lastChange ? [firstChange, lastChange].join(' / ') : null)
        );
      },
    },

    // ------------------------------------------------------------------ plain values (no v2 cell shows them)
    {
      key: F.CreatedAt,
      group: Group.AdditionalInfo,
      header: 'AllExpedition.createdAt',
      headerDefault: 'Vytvořeno',
      sortField: 'createdDateInUtc',
      minWidth: 140,
      render: row => text(row.createdAt ? stringDateTimeWithLocaleFromApiStringInTz(row.createdAt, ctx.timezoneId) : null),
    },
    {
      key: F.Folder,
      group: Group.AdditionalInfo,
      header: 'generic.folder',
      headerDefault: 'Folder',
      minWidth: 130,
      // RD-2977: an expedition can sit in several folders
      render: row => text(row.folderNames?.length ? row.folderNames.join(', ') : null),
    },
    {
      key: F.CustomsLoadingNumber,
      group: Group.AdditionalInfo,
      header: 'AllExpedition.mrnNumber',
      headerDefault: 'Mrn number',
      minWidth: 140,
      render: row => text(row.customsLoadingNumber),
    },
    {
      key: F.LadenFactor,
      group: Group.AdditionalInfo,
      header: 'generic.ladenFactor',
      headerDefault: 'Laden factor',
      minWidth: 100,
      render: row => text(row.ladenFactor),
    },
    {
      key: F.Roundtrip,
      group: Group.AdditionalInfo,
      header: 'generic.roundtrip',
      headerDefault: 'Roundtrip',
      minWidth: 100,
      render: row => flagMark(row.isRoundtrip),
    },
    {
      key: F.PartialDelivery,
      group: Group.AdditionalInfo,
      header: 'generic.partialDelivery',
      headerDefault: 'Partial delivery',
      minWidth: 100,
      render: row => flagMark(row.allowPartialDelivery),
    },
  ];
};

/**
 * Where the tender stands, as the v2 tender cell says it once no carrier is assigned yet: replaced by
 * a manual shipment, open with offers, manual mode, or nobody contacted. With a carrier assigned the
 * price and carrier fields say it, so this stays empty. Keeps the cell's hidden test ids.
 */
const TenderStatus: React.FC<{ row: Row; ctx: ExpeditionFieldContext }> = ({ row, ctx }) => {
  const { tr } = ctx;
  // Bar colours are the theme's: replaced-by-manual offers in alert, open-tender offers in success.
  const progress = (offers: number, total: number, barClass: string) =>
    total === 0 ? null : (
      <div className="flex items-center gap-2 w-full">
        <span>
          {offers} {tr('AllExpedition.responsesShort', 'odpov.')}
        </span>
        <div className="bg-content-quaternary h-2 w-[90px] relative shrink-0 overflow-hidden">
          <div className={clsx('h-2 transition-[width] duration-300', barClass)} style={{ width: `${Math.min(100, (offers / total) * 100)}%` }} />
        </div>
        <span>{tr('AllExpedition.fromTotal', 'z {{count}}', { count: total })}</span>
      </div>
    );

  let content: React.ReactNode = null;
  if (row.assignedCarrierName) {
    content = null;
  } else if (row.isManualExpedition && row.expeditionToId && row.distribution?.quotesCount > 0) {
    content = (
      <>
        <div>{tr('AllExpedition.replacedByManual', 'Nahrazeno manuální expedicí')}</div>
        {progress(row.distribution.quotesOfferCount, row.distribution.quotesCount, 'bg-complementary-alert')}
      </>
    );
  } else if (row.expeditionStatus === ExpeditionStatusEnum.ASSIGNING && row.distribution?.quotesCount > 0) {
    const validUntil = dayjsFromApiUtcInTz(row.distribution.utcEnd, ctx.timezoneId);
    content = (
      <>
        {row.distribution.isExpiredDistribution ? (
          <div className="text-complementary-error flex items-center gap-1">
            <AlertCircle width={16} height={16} style={{ marginRight: '0.25rem' }} />
            {validUntil?.isValid()
              ? tr('AllExpedition.tenderExpired', 'Vypršelo {{date}}', { date: validUntil.format('DD.MM.YYYY') })
              : tr('AllExpedition.tenderExpiredInvalidDate', 'Vypršelo (neplatné datum)')}
          </div>
        ) : (
          <div>
            {validUntil?.isValid()
              ? tr('AllExpedition.tenderValidUntil', 'Platnost do {{date}}', { date: validUntil.format('DD.MM HH:mm') })
              : tr('AllExpedition.tenderValidUntilInvalidDate', 'Platnost do (neplatné datum)')}
          </div>
        )}
        <COTimezone timezoneId={ctx.timezoneId} />
        {progress(row.distribution.quotesOfferCount, row.distribution.quotesCount, 'bg-complementary-success')}
      </>
    );
  } else if (row.isManualExpedition) {
    content = (
      <div className="inline-block px-3 py-1 rounded border border-solid border-content-gr2 text-content-secondary font-medium leading-[1.2] text-center whitespace-nowrap bg-base-quaternary">
        {tr('AllExpedition.manualMode', 'Manuální mód')}
      </div>
    );
  } else if (!row.distribution || row.distribution.quotesCount === 0) {
    content = <div className={muted}>{tr('AllExpedition.noCarriersContacted', 'Nejsou oslovení dopravci')}</div>;
  }

  return (
    <div
      className="flex flex-col items-start gap-1"
      data-testid={`customer-expeditions-row-${row.applicationId}-tender`}
      data-expired={row.distribution?.isExpiredDistribution ? 'true' : 'false'}
      data-valid-until-iso={row.distribution?.expiredUntil ?? ''}
      data-offers-count={String(row.distribution?.quotesOfferCount ?? '')}
      data-total-count={String(row.distribution?.quotesCount ?? '')}
    >
      {content}
      {/* The v2 cell's separate hooks, kept so the E2E specs read the tender the same way on both tables. */}
      <span
        data-testid={`customer-expeditions-row-${row.applicationId}-tender-expired`}
        data-expired={row.distribution?.isExpiredDistribution ? 'true' : 'false'}
        style={{ display: 'none' }}
      />
      <span
        data-testid={`customer-expeditions-row-${row.applicationId}-tender-validUntil`}
        data-valid-until-iso={row.distribution?.expiredUntil ?? ''}
        style={{ display: 'none' }}
      />
      <span
        data-testid={`customer-expeditions-row-${row.applicationId}-tender-offersCount`}
        data-offers-count={String(row.distribution?.quotesOfferCount ?? '')}
        style={{ display: 'none' }}
      />
      <span
        data-testid={`customer-expeditions-row-${row.applicationId}-tender-totalCount`}
        data-total-count={String(row.distribution?.quotesCount ?? '')}
        style={{ display: 'none' }}
      />
    </div>
  );
};
